<script setup lang="ts">
import {
  defineAsyncComponent,
  computed,
  ref,
  watch,
  onMounted,
} from 'vue';

import {
  onClickOutside,
  useEventListener,
  type MaybeElementRef,
} from '@vueuse/core';
import {
  ReviewWidgetBundle,
} from '@thehotelsnetwork/personalization-types';
import {
  SetupProvider,
  pluralRulesMap,
  isElementInShadowDom,
  getShadowHost,
} from '@thehotelsnetwork/widgets-core';

import ConfigProvider from '@/modules/configProvider/ConfigProvider.vue';

const props = defineProps<{
  config: ReviewWidgetBundle,
}>();

const FloatingDesktopReview = defineAsyncComponent(
  () => import('./views/FloatingDesktopReview.vue'),
);
const FloatingMobileReview = defineAsyncComponent(
  () => import('./views/FloatingMobileReview.vue'),
);
const InlineReview = defineAsyncComponent(
  () => import('./views/InlineReview.vue'),
);

const rootEl = ref<HTMLElement>();
const widget = ref<
  typeof FloatingDesktopReview
  | typeof FloatingMobileReview
  | typeof InlineReview
>();
const i18nMessages = ref({});

const componentType = computed(() => {
  switch (props.config.formatName) {
    case 'box':
      return FloatingDesktopReview;
    case 'inliner':
      return InlineReview;
    default:
      return FloatingMobileReview;
  }
});

const providerConfig = computed(() => ({
  payload: props.config,
  i18n: {
    locale: props.config.language,
    messages: i18nMessages.value,
    pluralRules: pluralRulesMap,
  },
}));
const htmlDirection = computed(() => (
  props.config.isRightAlign ? 'rtl' : 'ltr'
));

function collapse() {
  if (widget.value?.collapse) {
    widget.value.collapse();
  }
}


watch(() => props.config.language, async (newLang) => {
  try {
    const currentLangMessages = await import(`./locales/${newLang}.json`);
    i18nMessages.value = {
      [newLang]: currentLangMessages.default,
    };
  } catch (error) {
    i18nMessages.value = {};
    console.warn('THN: Error loading i18n messages');
  }
}, { immediate: true });

onMounted(() => {
  if (rootEl.value && isElementInShadowDom(rootEl.value)) {
    useEventListener(
      window,
      'click',
      (event) => {
        if (!rootEl.value) return;
        if (event.target !== getShadowHost(rootEl.value)) {
          collapse();
        }
      },
      { passive: true },
    );
  } else {
    onClickOutside(
      widget as MaybeElementRef,
      collapse,
      {
        ignore: ['.thn-widget-modal'],
      },
    );
  }
});

defineExpose({
  collapse,
});
</script>

<template>
  <div
    ref="rootEl"
    class="thn--widget thn--widget-review root w-full"
  >
    <SetupProvider
      :config="providerConfig"
    >
      <ConfigProvider
        :config="props.config"
        v-slot="{ config: transformedConfig }"
      >
        <component
          ref="widget"
          :is="componentType"
          :config="transformedConfig"
        />
      </ConfigProvider>
    </SetupProvider>
  </div>
</template>

<style scoped lang="postcss">
@import './styles/index.css';
.thn--widget {
  direction: v-bind('htmlDirection');
  text-align: start;
}
</style>
